import {
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form940Scha = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 940 - Schedule A | Instructions & Filing Guide"
          description="Find instructions for completing Form 940 - Schedule A. Learn how to file accurately and meet your federal unemployment tax obligations."
          keywords=""
          canonical="resources/employment-tax/form-940-schedule-a/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 940 </span>
          </span>{' '}
          Schedule A
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-940-schedule-a/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 940 Schedule A at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <b>Form 940 Schedule A</b>
                        {' is used by employers who '}
                        <b>
                          paid state unemployment taxes in more than one state
                        </b>
                        {' or paid wages in a '}
                        <b>credit reduction state</b>
                        {
                          '. Schedule A identifies the states where state unemployment taxes were paid and helps calculate any applicable FUTA credit reduction. '
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <b>How to Complete:</b>
                        {' Check the applicable boxes on '}
                        <b>Form 940</b>
                        {
                          ', complete Schedule A by marking each state where you paid state unemployment '
                        }
                        taxes and
                        {
                          ' enter the FUTA taxable wages and credit reduction amounts for applicable states. Add the credit reduction amounts and report the total on '
                        }
                        <b>Form 940, line 11</b>
                        {'. '}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <b>2025 Credit Reduction States:</b>
                        {' For the 2025 tax year, '}
                        <b>California has a 1.2% credit reduction rate</b>
                        {', while the '}
                        <b>
                          U.S. Virgin Islands has a 4.5% credit reduction rate
                        </b>
                        {
                          '. Employers with wages subject to these reductions may owe '
                        }
                        additional
                        {' FUTA tax. '}
                      </span>
                    </li>
                  </Box>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                      If you, as an employer, pay unemployment taxes in multiple
                      states or operate in a state subject to a credit
                      reduction, you are required to file{' '}
                      <b>Form 940 Schedule A</b>.
                    </Typography>
                  </Box>
                  <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  />
                </Stack>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When to Use Schedule A (Form 940)
                </Typography>
                <ol style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b> Multiple States:</b> If you paid state unemployment tax
                    in more than one state.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Credit Reduction State:</b> If you paid wages in any
                    state that is subject to credit reduction.
                  </li>
                </ol>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Multi-state Employers
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Multi-state employers</b> are those who pay unemployment
                  taxes in multiple states because they hire employees from
                  various locations. You should mark the checkbox indicating
                  that your business is a multi-state employer on Line 1b of
                  Form 940.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Form 940 Credit Reduction
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  A state that has not repaid funds borrowed from the federal
                  government to cover unemployment benefits is known as a credit
                  reduction state. The U.S. <b>Department of Labor</b>{' '}
                  designates these states. If an employer pays wages in a credit
                  reduction state, they must pay additional federal unemployment
                  tax when filing Form 940 due to the state’s unemployment tax
                  laws.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Credit Reduction Rates
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Below are the credit reduction rates for 2025:
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            State
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            2025 Credit Reduction Rate
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>California</TableCell>

                          <TableCell>0.012 (1.2%) </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>U.S. Virgin Islands</TableCell>

                          <TableCell>0.045 (4.5% )</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" sx={{ mb: 0 }}>
                    Source: IRS{' '}
                    <Link
                      href="https://www.irs.gov/pub/irs-pdf/f940sa.pdf"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 940 Schedule A
                    </Link>{' '}
                    Instructions
                  </Typography>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Attaching Schedule A Details to IRS Form 940
                </Typography>
                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Multi-state Employers:</b> Check the box on line 1b of
                    Form 940. Fill out Schedule A and attach it to your Form
                    940.
                    <picture>
                      <source srcSet="/assets/images/940-scha-line1b-img.webp" />
                      <source srcSet="/assets/images/940-scha-line1b-img.png" />
                      <img
                        src="/assets/images/940-scha-line1b-img.png"
                        alt="Check the box on line 1b of Form 940. Fill out Schedule A and attach it to your Form 940."
                        width={726}
                        height={110}
                        className="new-form-employ"
                        style={{ border: '2px solid #000' }}
                      />
                    </picture>
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Credit Reduction States:</b> Check the box on Line 2,
                    complete Schedule A, and attach it to your Form 940
                    submission.
                    <picture>
                      <source srcSet="/assets/images/940-scha-line2-img.webp" />
                      <source srcSet="/assets/images/940-scha-line2-img.png" />
                      <img
                        src="/assets/images/940-scha-line2-img.png"
                        alt="Check the box on Line 2, complete Schedule A, and attach it to your Form 940 submission"
                        width={726}
                        height={110}
                        className="new-form-employ"
                        style={{ border: '2px solid #000' }}
                      />
                    </picture>
                  </li>
                </ol>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Step-by-Step Guide to Filling Out Schedule A
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: -1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 1: Employer Information
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 16, fontWeight: 600 }}>
                    Employer Identification Number (EIN)
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}>Name </span>(as shown on
                    Form 940)
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: -1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 2: Mark States
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 16 }}>
                    Mark an &quot;X&quot; next to each state (including D.C.,
                    Puerto Rico, and the U.S. Virgin Islands) where you paid
                    state unemployment taxes this year, even if the credit
                    reduction rate is zero.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Ensure you have a state reporting number for each state. If
                    not, contact the state unemployment agency.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: -1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 3: Credit Reduction
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 16 }}>
                    Identify states with a credit reduction rate above zero
                    (e.g., California 1.2%, U.S. Virgin Islands 4.5%).
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Enter the total FUTA taxable wages paid in these states in
                    the &quot;FUTA Taxable Wages&quot; box, excluding wages not
                    subject to state unemployment tax.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Multiply the FUTA taxable wages by the state&apos;s credit
                    reduction rate.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Enter the result in the &quot;Credit Reduction&quot; box.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: -1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 4: Total Credit Reduction
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Add up all amounts in the &quot;Credit Reduction&quot;
                    boxes.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Enter the total in the &quot;Total Credit Reduction&quot;
                    box and on Form 940, line 11.
                  </li>
                </ul>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-evenly"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        TaxZerone will automatically calculate the taxes when
                        you e-file through the platform.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 140, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=940scha`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  State Abbreviations
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The following table provides the two-letter postal
                  abbreviations used on Schedule A:
                </Typography>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={{ xs: -0.1, md: 2 }}
                  sx={{ mb: 4, '& td': { py: 1 } }}
                >
                  <TableContainer
                    sx={{
                      border: '1px solid #828282',
                      borderBottom: { xs: '0', md: '1px solid #828282' },
                    }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            State
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Abbreviation
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ width: 350 }}>Alabama</TableCell>
                          <TableCell sx={{ width: 250 }}>AL</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Alaska</TableCell>
                          <TableCell>Ak</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Arizona</TableCell>
                          <TableCell>AZ</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Arkansas</TableCell>
                          <TableCell>AR</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>California</TableCell>
                          <TableCell>CA</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Colorado</TableCell>
                          <TableCell>CO</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Connecticut</TableCell>
                          <TableCell>CT</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Delaware</TableCell>
                          <TableCell>DE</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>District of Columbia</TableCell>
                          <TableCell>DC</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Florida</TableCell>
                          <TableCell>FL</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Georgia</TableCell>
                          <TableCell>GA</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Hawaii</TableCell>
                          <TableCell>HI</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Idaho</TableCell>
                          <TableCell>ID</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Illinois</TableCell>
                          <TableCell>IL</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Indiana</TableCell>
                          <TableCell>IN</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Iowa</TableCell>
                          <TableCell>IA</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Kansas</TableCell>
                          <TableCell>KS</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Kentucky</TableCell>
                          <TableCell>KY</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Louisiana</TableCell>
                          <TableCell>LA</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Maine</TableCell>
                          <TableCell>ME</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Maryland</TableCell>
                          <TableCell>MD</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Massachusetts</TableCell>
                          <TableCell>MA</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Michigan</TableCell>
                          <TableCell>MI</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Minnesota</TableCell>
                          <TableCell>MN</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Mississippi</TableCell>
                          <TableCell>MS</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Missouri</TableCell>
                          <TableCell>MO</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TableContainer
                    sx={{
                      border: '1px solid #828282',
                      borderTop: { xs: '0', md: '1px solid #828282' },
                    }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <Hidden mdDown>
                        <TableHead>
                          <TableRow
                            sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                          >
                            <TableCell
                              sx={{
                                color: '#444',
                                width: 300,
                                fontWeight: 600,
                              }}
                            >
                              State
                            </TableCell>
                            <TableCell
                              sx={{
                                color: '#444',
                                width: 300,
                                fontWeight: 600,
                              }}
                            >
                              Abbreviation
                            </TableCell>
                          </TableRow>
                        </TableHead>
                      </Hidden>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ width: { xs: 215, sm: 300 } }}>
                            Montana
                          </TableCell>
                          <TableCell sx={{ width: 300 }}>MT</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Nebraska</TableCell>
                          <TableCell>NE</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Nevada</TableCell>
                          <TableCell>NV</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>New Hampshire</TableCell>
                          <TableCell>NH</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>New Jersey</TableCell>
                          <TableCell>NJ</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>New Mexico</TableCell>
                          <TableCell>NM</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>New York</TableCell>
                          <TableCell>NY</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>North Carolina</TableCell>
                          <TableCell>NC</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>North Dakota</TableCell>
                          <TableCell>ND</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Ohio</TableCell>
                          <TableCell>OH</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Oklahoma</TableCell>
                          <TableCell>OK</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Oregon</TableCell>
                          <TableCell>OR</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Pennsylvania</TableCell>
                          <TableCell>PA</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Rhode Island</TableCell>
                          <TableCell>RI</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>South Carolina</TableCell>
                          <TableCell>SC</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>South Dakota</TableCell>
                          <TableCell>SD</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Tennessee</TableCell>
                          <TableCell>TN</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Texas</TableCell>
                          <TableCell>TX</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Utah</TableCell>
                          <TableCell>UT</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Vermont</TableCell>
                          <TableCell>VT</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Virginia</TableCell>
                          <TableCell>VA</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Washington</TableCell>
                          <TableCell>WA</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>West Virginia</TableCell>
                          <TableCell>WV</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Wisconsin</TableCell>
                          <TableCell>WI</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Wyoming</TableCell>
                          <TableCell>WY</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Puerto Rico</TableCell>
                          <TableCell>PR</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>U.S. Virgin Islands</TableCell>
                          <TableCell>VI</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Conclusion
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Ensure you follow these steps to correctly complete{' '}
                  <b>Form 940 Schedule A</b>. Proper compliance helps avoid
                  penalties and ensures accurate tax reporting.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
    </AppLayout>
  );
};

export default Form940Scha;
